// FocusedLayout — the ROUTE layout for focused flows (ADR-036 §9): it wires the
// route concerns around the presentational FocusedFrame shell — ProtectedRoute gate,
// the project SCOPE (the project name when project-scoped, the RubixEMS wordmark on a
// platform / project-less flow), the project strips (a demo / trial project says
// so inside a wizard too), the close target, and main-focus-on-navigate.
// FocusedFrame owns the chrome bar + centered body + the sticky footer slot.
//
// FocusedFooter / useFocusedFooter live in FocusedFrame and are re-exported here so
// existing consumers (Stepper) keep importing them from this module.
//
// CLOSE (the ✕ and Escape — both funnel through onClose): every focused ROUTE
// declares its deep-link exit as `handle: { closeTo: (params) => path }`. With
// in-app history the exit is ONE pop to the entry that led into the flow
// (`lib/historyEntry` — the flow's entries carry where its visit began, so
// nothing inside it is ever replayed); a deep-linked visitor has nothing to pop
// to, so the layout resolves closeTo and REPLACES the flow's entry with it —
// never a push, so Back never reopens the flow. Each page's route-leave dirty
// guard holds either move. Declaring it beside the route (not inside the page)
// means a new focused flow cannot forget it silently.

import { useCallback, useRef } from 'react';
import { Link as RouterLink, Outlet, useMatches, useNavigate, type Params } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../auth/ProtectedRoute.js';
import { useFocusMainOnNavigate } from '../../hooks/useFocusMainOnNavigate.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useHistoryEntry } from '../../lib/historyEntry.js';
import { currentIndex } from '../../lib/entryState.js';
import { useProject } from '../../hooks/useProject.js';
import { LogoWordmark } from '../ui/Logo.js';
import { FocusedFrame } from './FocusedFrame.js';
import { ProjectStrips } from './ProjectStrips.js';

// Re-exported so Stepper (and any focused page) keeps importing the footer slot from
// this module; the slot itself is owned by FocusedFrame.
export { FocusedFooter, useFocusedFooter } from './FocusedFrame.js';

/** The route-handle contract of a focused route: where its ✕/Escape exits to. */
export interface FocusedRouteHandle {
  closeTo: (params: Params) => string;
}

/** THE exit of a focused flow — every programmatic leave (Save and exit ·
 *  Done · Cancel · post-submit fallback) AND the frame's ✕/Escape use this,
 *  never a hardcoded list path. In-app history → **browser-back**: the history
 *  entry carries the origin's FULL URL — including the nuqs shallow
 *  `replaceState` view-state updates (filters · sort · grouping · search,
 *  ADR-037) that never pass through the router, which is why any
 *  record-the-location approach under-restores. The pop also consumes the
 *  focused entry, so back never re-opens the flow (ADR-036 §2). Deep link
 *  (no in-app history) → the route's `closeTo` handle, replacing the focused
 *  entry. The same pattern as the EntityHeader back chevron. Deliberate
 *  FORWARD navigations (e.g. to a just-created entity's page) are not exits —
 *  those stay explicit `navigate(...)` calls. */
export function useFocusedExit(): () => void {
  const navigate = useNavigate();
  const matches = useMatches();
  // The DEEPEST match carrying a closeTo handle is the flow (the leaf route); its base path is the entry's scope.
  const leaf = [...matches].reverse().find((m) => (m.handle as Partial<FocusedRouteHandle> | undefined)?.closeTo);
  const { exit } = useHistoryEntry(`focused:${leaf?.pathname ?? matches[matches.length - 1]?.pathname ?? '/'}`);
  return useCallback(() => {
    if (leaf) {
      const handle = leaf.handle as FocusedRouteHandle;
      exit(handle.closeTo(leaf.params));
      return;
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[FocusedLayout] focused route without handle.closeTo — deep-linked close has nowhere to go; falling back to history, else home');
    }
    // A flow that forgot its closeTo: a plain pop while there is history to pop; home only without.
    if (currentIndex() > 0) navigate(-1);
    else navigate('/', { replace: true });
  }, [navigate, leaf, exit]);
}

export function FocusedLayout() {
  const { t } = useTranslation('nav');
  const projectId = useOptionalProjectParams();
  const project = useProject(projectId ?? '');
  const projectName = project.data?.display_name ?? project.data?.name ?? '';
  const mainRef = useRef<HTMLElement>(null);
  useFocusMainOnNavigate(mainRef);

  // ✕ + Escape ride the same exit primitive as every in-page exit button.
  const onClose = useFocusedExit();

  const scope =
    projectId && projectName ? (
      projectName
    ) : (
      <LogoWordmark height={28} className="text-[var(--color-text-primary)]" />
    );

  return (
    <FocusedFrame
      scope={scope}
      onClose={onClose}
      mainRef={mainRef}
      strips={<ProjectStrips />}
      renderHome={(mark) => (
        <RouterLink
          to="/"
          aria-label={t('homeLink')}
          className="flex shrink-0 items-center rounded-sm outline-none focus-ring"
        >
          {mark}
        </RouterLink>
      )}
    >
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    </FocusedFrame>
  );
}
